import { displayDate } from '../../../../../../../../../utils/globals/date'
import { TwinDictionary } from '../../../../../../../../../utils/globals/dictionary'
import useTwinTranslation from '../../../../../../../../../utils/hooks/useTwinTranslation'
import { InputTableLayoutData } from '../../../../../../../../../baseComponents/TwinTable/InputTableLayout/types'
import { SellDeliveryNoteLogModelType } from '@teinor/erp/types/company/order/sell/sellDeliveryNote/sellDeliveryNoteLog'
import { Fragment } from 'react'
import OrderStatusBar from '../../../../../../../../../specificComponents/inputTableLayouts/order/OrderLayout/OrderStatusBar'
import { getOrderStatusBarType } from '../../../../../../../../../specificComponents/inputTableLayouts/order/OrderLayout/OrderStatusBar/functions'
import { sellDeliveryNoteStatusInversed } from '@teinor/erp/types/company/order/sell/sellDeliveryNote'
import OrderStatusBarDropDown from '../../../../../../../../../specificComponents/inputTableLayouts/order/OrderLayout/OrderStatusBarDropDown'
import { faClock, faPenCircle } from '@fortawesome/pro-light-svg-icons'
import { faCheckCircle, faXmarkCircle } from '@fortawesome/pro-solid-svg-icons'


export interface SellDeliveryNoteStatusBarProps extends CreateStatusBarProps {}

const SellDeliveryNoteStatusBar: React.FC<SellDeliveryNoteStatusBarProps> = ({ completeData}) => {
    const { statuses } = useCreateStatusBar({ completeData })
    return (
        <OrderStatusBar statuses={statuses} currentOrderStatus={completeData?.header?.status} getOrderStatusBarType={getOrderStatusBarType} isCanceled={completeData?.header?.status === sellDeliveryNoteStatusInversed['refunded'] }/>
    )
}

interface CreateStatusBarProps {
    completeData: InputTableLayoutData | null
}

const useCreateStatusBar = ({ completeData }: CreateStatusBarProps) => {
    const { t } = useTwinTranslation()
    const sellDeliveryNoteStatus = completeData?.header.status
    const sellOrderStatus = completeData?.header.status
    const OrderLogs = completeData?.header?.OrderLogs
    const QuoteOrderId = completeData?.header?.QuoteOrderId
    const SellOrderId = completeData?.header?.SellOrderId
    let statuses: TwinDictionary[] = []
    const dictOrderLogs: TwinDictionary = {}
    for (const log of OrderLogs) {
        if (!dictOrderLogs[log.logType]) {
            dictOrderLogs[log.logType] = [log]
        } else {
            dictOrderLogs[log.logType] = [...dictOrderLogs[log.logType], log]
        }
    }
    if (SellOrderId) {
        statuses.push({
            id: -1,
            name: t('orderSell', 'Pedido'),
            logType: 0,
            color: 'bg-gray-33',
            icon: faPenCircle,
            className: 'text-gray-33',
            extraClassName: 'border border-gray-33',
            renderMessage: dictOrderLogs[0]?.length ? <RenderLogMessage logs={dictOrderLogs[0] || []} SellOrderId={SellOrderId} /> : null
        })
    } else if (QuoteOrderId) {
        statuses.push({
            id: -1,
            name: t('quoteOrder', 'Presupuesto'),
            logType: 0,
            color: 'bg-gray-33',
            icon: faPenCircle,
            className: 'text-gray-33',
            extraClassName: 'border border-gray-33',
            renderMessage: dictOrderLogs[0]?.length ? <RenderLogMessage logs={dictOrderLogs[0] || []} QuoteOrderId={QuoteOrderId} /> : null
        })
    }

    statuses.push({
        id: 0,
        name: t('draft', 'Borrador'),
        logType: 0,
        color: 'bg-gray-33',
        icon: faPenCircle,
        className: 'text-gray-33',
        extraClassName: 'border border-gray-33',
        renderMessage: dictOrderLogs[0]?.length ? <RenderLogMessage logs={dictOrderLogs[0] || []} /> : null
    })
    statuses.push(
        {
            id: sellDeliveryNoteStatus === sellDeliveryNoteStatusInversed['pendingUnits'] ? 2 : 1,
            name: t('pendingInvoiced', 'Facturación pte.'),
            logType: 1,
            color: 'bg-orange-DE',
            icon: faClock,
            className: 'text-orange-DE',
            extraClassName: 'border border-orange-DE',
            renderMessage: dictOrderLogs[1]?.length ? <RenderLogMessage logs={dictOrderLogs[1] || []} /> : null
        }
    )
    statuses.push({
        id: 3,
        name: t('finished', 'Finalizado'),
        logType: 2,
        color: 'bg-green-43',
        icon: faCheckCircle,
        className: 'text-green-43',
        renderMessage: dictOrderLogs[2]?.length ? <RenderLogMessage logs={dictOrderLogs[2] || []} /> : null
    })
    if (completeData?.header?.status === sellDeliveryNoteStatusInversed['refunded']) {
        statuses.push({
            id: 5,
            name: t('refunded', 'Devuelto'),
            renderMessage: dictOrderLogs[3]?.length ? <RenderLogMessage logs={dictOrderLogs[3] || []} /> : null,
            logType: 3,
            color: 'bg-red-BA',
            icon: faXmarkCircle,
            className: 'text-red-BA',
        })
    } 
    return { statuses, sellOrderStatus }
}

interface RenderLogMessageProps {
    logs: SellDeliveryNoteLogModelType[]
    QuoteOrderId?: number
    SellOrderId?: number
}

const RenderLogMessage: React.FC<RenderLogMessageProps> = ({ logs, QuoteOrderId, SellOrderId }) => {
    const { tVars } = useTwinTranslation()
    const renderMessage: JSX.Element[] = []
    for (const log of logs) {
        const myExtraData = log?.extraData ? JSON.parse(log?.extraData) : null
        const options = { employeeTrigger: log.EmployeeTrigger?.fullname_short || '', date: displayDate(new Date(log.createdAt)), full_assigned_number: myExtraData?.full_assigned_number || null, 'interpolation': { 'escapeValue': false }}
        const dictSellDeliveryNoteLogsMessages: TwinDictionary = {
            0: tVars('createdDeliveryNoteLogMessage', '{{employeeTrigger}} creó el albarán el {{date}}', options),
            1: tVars('waitingInvoiceDeliveryNoteLogMessage', '{{employeeTrigger}} marcó el albarán como esperando facturación el {{date}}', options),
            2: tVars('finishedDeliveryNoteLogMessage', '{{employeeTrigger}} marcó el albarán como finalizado el {{date}}', options),
            3: tVars('createdDeliveryNoteByQuoteLogMessage', '{{employeeTrigger}} creó el albarán a partir del presupuesto {{full_assigned_number}}', options),
            4: tVars('createdDeliveryNoteBySellOrderLogMessage', '{{employeeTrigger}} creó el albarán a partir del pedido {{full_assigned_number}}', options),
            5: tVars('refundedDeliveryNoteLogMessage', '{{employeeTrigger}} marcó el albarán como devuelto el {{date}}', options),
        }
        let myLogType: number = log.logType
        if (log.logType === 0) {
            if (QuoteOrderId) {
                myLogType = 3
            }
            if (SellOrderId) {
                myLogType = 4
            }
        }
        if (log.logType === 3) {
            myLogType = 5
        }
        if (dictSellDeliveryNoteLogsMessages[log.logType]) {
            renderMessage.push(<div className='my-5' key={log.id} > {dictSellDeliveryNoteLogsMessages[myLogType]} </div>)
        }
    }
    if (!renderMessage.length) {
        return null
    }
    return (<Fragment>{renderMessage}</Fragment>)
}

export const SellDeliveryNoteStatusBarNew: React.FC<SellDeliveryNoteStatusBarProps> = ({ completeData }) => {
    const { statuses, sellOrderStatus } = useCreateStatusBar({ completeData })
    return (
        <OrderStatusBarDropDown statuses={statuses} currentOrderStatus={completeData?.header.status} getOrderStatusBarType={getOrderStatusBarType} isAccepted={sellOrderStatus === sellDeliveryNoteStatusInversed['accepted']} isCanceled={sellOrderStatus === sellDeliveryNoteStatusInversed['refunded']} />
    )
}

export default SellDeliveryNoteStatusBar